/**
 * Mail-Baukasten: baut aus einfachen Angaben eine Mail im Randori-Haus-Layout
 * (Brand-Design v2.1), genau so, wie die Mails seit August 2026 aussehen —
 * Stundenplan, Samurai-Ausstellung, Weihnachtsfeier, Kinderturnier.
 *
 * Wer eine Vorlage im Assistenten ausfuellt, sieht nie HTML. Er beantwortet
 * Fragen und fuellt Felder; dieses Modul setzt daraus die Bausteine zusammen:
 * Logo-Leiste, roter Kopfbereich, Anrede, Text, Info-Boxen, Eckdaten, Knopf,
 * Signatur, Hinweis-Block und die Pflicht-Fusszeile.
 *
 * Regeln aus den bisherigen Mails, die hier fest eingebaut sind:
 *  - Jede Flaeche mit Hintergrund traegt bgcolor + Klasse (Dark-Mode-Schutz).
 *  - Akzent-Streifen und Hintergrund liegen auf der inneren <td>, nie auf der
 *    Tabelle (Brevo entfernt sie dort).
 *  - Keine rgba()-Farben fuer Text, Links immer mit farbigem <span> darin.
 *  - Knoepfe hell mit dunkelroter Schrift, auch auf dem roten Block.
 *  - Bilder mit height: auto.
 *  - Fusszeile mit Impressum und Abmelde-Link unveraendert.
 *
 * Alle Texte aus dem Formular werden hier maskiert. Erlaubt sind nur die
 * Formatierungen aus dem Texteditor (fett, kursiv, markiert, drei Farben und
 * zwei Groessen aus dem Brand-Design, siehe formatieren()) und Zeilenumbrueche;
 * Links gibt es nur ueber den Knopf (geprueftes https).
 */

const FONT = 'Arial, Helvetica, sans-serif';
const LOGO_URL = 'https://img.mailinblue.com/10511231/images/content_library/original/6993264d73a970ef40c727be.png';

export const BRAND = {
  red: '#6D1723',
  accent: '#DC0D1D',
  ink: '#1A1414',
  cream: '#FAF6EE',
  beige: '#D4C9B5',
  grey: '#575E62',
  white: '#FFFFFF',
} as const;

// ── Datenmodell ──────────────────────────────────────────────────────────────

export interface MailHero {
  /** Symbol ueber der Ueberschrift (ein bis zwei Emojis) — leer: keins. */
  emoji: string;
  /** Kleine Kapsel ueber dem Titel, z. B. „Kurzfristige Änderung · Tegel". */
  badge: string;
  title: string;
  /** Zeile unter dem Titel (fett, cremefarben). */
  subtitle?: string;
  /** Zweite Zeile, z. B. „⚡ Samstag, 5. Dezember · 12:00 Uhr". */
  highlight?: string;
  /** Optionales Bannerbild, 620 breit, zugeschnitten auf imageHeight (hoechstens 300). */
  imageUrl?: string;
  imageAlt?: string;
  imageHeight?: number;
  /** oben: direkt unter dem Logo, ueber dem roten Kopf · unten (Standard): unter dem Kopf */
  imagePosition?: 'oben' | 'unten';
}

export interface FactRow { label: string; value: string; sub?: string }

export type MailSection =
  | { type: 'heading'; text: string }
  | { type: 'text'; text: string; align?: 'center' | 'left' }
  | { type: 'box'; tone?: 'info' | 'important'; title: string; text: string }
  | { type: 'cards'; heading?: string; items: Array<{ title: string; text: string }> }
  | { type: 'facts'; heading?: string; rows: FactRow[] }
  | { type: 'steps'; heading?: string; items: string[] }
  | { type: 'list'; heading?: string; items: string[] }
  | { type: 'cta'; title: string; text?: string; buttonText: string; url: string; footnote?: string };

export interface MailDoc {
  /** Seitentitel (nur im HTML-Kopf). */
  title: string;
  /** Unsichtbarer Vorschautext im Posteingang. */
  preheader?: string;
  hero: MailHero;
  /** Anrede in Dunkelrot, z. B. „Liebe Mitglieder, liebe Eltern 👋,". */
  greeting?: string;
  /** Einleitung; Absaetze durch Leerzeile getrennt. */
  intro?: string;
  sections: MailSection[];
  /** Schlusssatz vor der Signatur. */
  closing?: string;
  signature: { greeting: string; team: string };
  /** Hinweis-Block vor der Fusszeile. */
  contact: { email?: string; place?: string; text?: string };
  /** Emoji-Stufe der Mail; „ohne" laesst auch das Warnzeichen im Hinweis-Block weg. */
  emojis?: 'ohne' | 'normal' | 'viele';
}

/** Hoechste Bannerhoehe (bei 620 px Breite), damit der Kopf der Mail sichtbar bleibt. */
export const BANNER_MAX_HOEHE = 300;
export const BANNER_MIN_HOEHE = 140;

// ── Text-Helfer ──────────────────────────────────────────────────────────────

export function escapeHtml(s: string): string {
  return (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Farben und Groessen, die der Texteditor anbietet — nur aus dem Brand-Design. */
export const TEXT_FARBEN = {
  rot: { label: 'Randori-Rot', hex: BRAND.red, klasse: 'dark-text-red' },
  signal: { label: 'Signalrot', hex: BRAND.accent, klasse: 'dark-text-accent' },
  grau: { label: 'Grau', hex: BRAND.grey, klasse: 'dark-text-grey' },
} as const;
export type TextFarbe = keyof typeof TEXT_FARBEN;

export const TEXT_GROESSEN = {
  klein: { label: 'Klein', css: 'font-size: 13px; line-height: 1.6;' },
  gross: { label: 'Groß', css: 'font-size: 20px; line-height: 1.5;' },
} as const;
export type TextGroesse = keyof typeof TEXT_GROESSEN;

/** Markierung wie mit dem Textmarker: tiefes Beige, dunkle Schrift. */
export const MARKIERT =`background-color: ${BRAND.beige}; color: ${BRAND.ink}; padding: 0 3px;`;

/**
 * Formatierungen aus dem Texteditor in Mail-HTML (inline-Styles, Dark-Mode-
 * Klassen): [b] [i] [mark] [farbe=rot|signal|grau] [klein] [gross] — und das
 * alte **fett**. Laeuft nach dem Maskieren; Unbekanntes bleibt Text.
 */
function formatieren(html: string): string {
  let out = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  // Mehrere Durchgaenge fuer verschachtelte Formatierungen (fett in rot …).
  for (let i = 0; i < 6; i++) {
    const vorher = out;
    out = out
      .replace(/\[b\]([\s\S]*?)\[\/b\]/g, '<strong>$1</strong>')
      .replace(/\[i\]([\s\S]*?)\[\/i\]/g, '<em>$1</em>')
      .replace(/\[mark\]([\s\S]*?)\[\/mark\]/g, `<span class="dark-mark-fix" style="${MARKIERT}">$1</span>`)
      .replace(/\[farbe=(rot|signal|grau)\]([\s\S]*?)\[\/farbe\]/g, (_m, f: TextFarbe, t: string) =>
        `<span class="${TEXT_FARBEN[f].klasse}" style="color: ${TEXT_FARBEN[f].hex};">${t}</span>`)
      .replace(/\[(klein|gross)\]([\s\S]*?)\[\/\1\]/g, (_m, g: TextGroesse, t: string) =>
        `<span style="${TEXT_GROESSEN[g].css}">${t}</span>`);
    if (out === vorher) break;
  }
  return out;
}

/** Maskiert, setzt die erlaubten Formatierungen und Zeilenumbrueche. */
export function inline(s: string | null | undefined): string {
  return formatieren(escapeHtml((s ?? '').trim())).replace(/\r?\n/g, '<br>');
}

/** Text ohne Formatierungszeichen — fuer Betreff, Vorschautext, Seitentitel. */
export function ohneFormat(s: string | null | undefined): string {
  return (s ?? '')
    .replace(/\*\*([^*\n]+)\*\*/g, '$1')
    .replace(/\[(\/?)(b|i|mark|klein|gross)\]/g, '')
    .replace(/\[farbe=(rot|signal|grau)\]|\[\/farbe\]/g, '');
}

/** Absaetze an Leerzeilen trennen. */
export function paragraphs(s: string | undefined): string[] {
  return (s ?? '')
    .split(/\r?\n\s*\r?\n/)
    .map(p => p.trim())
    .filter(Boolean);
}

/** Nur echte https-Links in den Knopf lassen. */
export function safeUrl(url: string | undefined): string | null {
  const u = (url ?? '').trim();
  if (!/^https:\/\/[^\s"'<>]+\.[^\s"'<>]+/i.test(u)) return null;
  try {
    const parsed = new URL(u);
    if (parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

// ── Bausteine ────────────────────────────────────────────────────────────────

function row(inner: string, padding: string, extraStyle = ''): string {
  return `<tr>
<td bgcolor="#FFFFFF" class="dark-card-fix" style="padding: ${padding}; background-color: #FFFFFF;${extraStyle}">
${inner}
</td>
</tr>`;
}

function bodyParagraph(html: string, opts: { align?: 'center' | 'left'; last?: boolean } = {}): string {
  const align = opts.align === 'left' ? 'left' : 'center';
  return `<p style="font-size: 16px; line-height: 1.8; margin: 0 0 ${opts.last ? '0' : '16px'}; font-family: ${FONT}; color: #1A1414; text-align: ${align};" class="dark-text-body">
${html}
</p>`;
}

function sectionHeading(text: string): string {
  return `<p style="color: #1A1414; font-size: 19px; font-weight: 800; margin: 0 0 18px; font-family: ${FONT};" class="dark-text-dark">${inline(text)}</p>`;
}

function accentBox(title: string, text: string, tone: 'info' | 'important', marginBottom: number): string {
  const border = tone === 'important' ? BRAND.accent : BRAND.red;
  const titleHtml = (title ?? '').trim()
    ? `<p style="color: #6D1723; font-size: 15px; font-weight: 700; margin: 0 0 4px; font-family: ${FONT};" class="dark-text-red">${inline(title)}</p>\n`
    : '';
  const textHtml = paragraphs(text)
    .map((p, i, all) => `<p style="color: #444444; font-size: 14px; line-height: 1.7; margin: 0 0 ${i === all.length - 1 ? '0' : '10px'}; font-family: ${FONT};" class="dark-text-body">${inline(p)}</p>`)
    .join('\n');
  return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="margin-bottom: ${marginBottom}px; border-collapse: collapse;">
<tbody><tr>
<td bgcolor="#FAF6EE" class="dark-bg-fix" style="background-color: #FAF6EE; border-left: 4px solid ${border}; padding: 14px 20px;">
${titleHtml}${textHtml}
</td>
</tr></tbody>
</table>`;
}

function factsTable(rows: FactRow[]): string {
  const trs = rows
    .filter(r => (r.label ?? '').trim() && (r.value ?? '').trim())
    .map(r => {
      const sub = r.sub?.trim()
        ? `<br><span style="font-weight: 400; color: #555555;" class="dark-text-body">${inline(r.sub)}</span>`
        : '';
      return `<tr>
<td style="padding: 6px 0; font-size: 14px; color: #888888; width: 34%; vertical-align: top; font-family: ${FONT};">${inline(r.label)}</td>
<td style="padding: 6px 0; font-size: 14px; color: #1A1414; font-weight: 700; font-family: ${FONT};" class="dark-text-dark">${inline(r.value)}${sub}</td>
</tr>`;
    })
    .join('\n');
  return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
<tbody><tr>
<td bgcolor="#FAF6EE" class="dark-bg-fix" style="background-color: #FAF6EE; border-left: 4px solid #6D1723; padding: 18px 22px;">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
<tbody>
${trs}
</tbody>
</table>
</td>
</tr></tbody>
</table>`;
}

function numberedSteps(items: string[]): string {
  const trs = items
    .filter(i => i.trim())
    .map((item, idx) => `<tr>
<td width="34" style="padding: 6px 12px 6px 0; vertical-align: top; font-family: ${FONT};">
<table role="presentation" cellspacing="0" cellpadding="0" border="0"><tbody><tr>
<td align="center" bgcolor="#6D1723" class="dark-hero-red-fix" style="width: 26px; height: 26px; border-radius: 13px; background-color: #6D1723; color: #FFFFFF; font-size: 13px; font-weight: 800; font-family: ${FONT};"><span style="color: #FFFFFF;" class="dark-text-white">${idx + 1}</span></td>
</tr></tbody></table>
</td>
<td style="padding: 8px 0; font-size: 15px; line-height: 1.6; color: #1A1414; font-family: ${FONT};" class="dark-text-body">${inline(item)}</td>
</tr>`)
    .join('\n');
  return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
<tbody>
${trs}
</tbody>
</table>`;
}

function bulletList(items: string[]): string {
  const lis = items
    .filter(i => i.trim())
    .map(item => `<tr>
<td width="22" style="padding: 5px 0; vertical-align: top; color: #6D1723; font-size: 15px; font-weight: 800; font-family: ${FONT};" class="dark-text-red">✓</td>
<td style="padding: 5px 0; font-size: 15px; line-height: 1.6; color: #1A1414; font-family: ${FONT};" class="dark-text-body">${inline(item)}</td>
</tr>`)
    .join('\n');
  return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
<tbody><tr>
<td bgcolor="#FAF6EE" class="dark-bg-fix" style="background-color: #FAF6EE; border-left: 4px solid #6D1723; padding: 14px 20px;">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
<tbody>
${lis}
</tbody>
</table>
</td>
</tr></tbody>
</table>`;
}

function ctaBlock(s: Extract<MailSection, { type: 'cta' }>): string {
  const url = safeUrl(s.url);
  const text = s.text?.trim()
    ? `<p style="color: #FAF6EE; font-size: 14px; line-height: 1.6; margin: 0 0 22px; font-family: ${FONT};" class="dark-text-cream">${inline(s.text)}</p>\n`
    : '';
  const button = url
    ? `<table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin: 0 auto;">
<tbody><tr>
<td align="center" bgcolor="#FAF6EE" class="dark-btn-fix" style="border-radius: 8px; background-color: #FAF6EE;">
<a href="${escapeHtml(url)}" style="display: inline-block; padding: 15px 40px; color: #6D1723; font-size: 16px; font-weight: 800; text-decoration: none; font-family: ${FONT};"><span style="color: #6D1723;" class="dark-text-red">${inline(s.buttonText)} →</span></a>
</td>
</tr></tbody>
</table>`
    : '';
  const foot = s.footnote?.trim()
    ? `\n<p style="color: #D4C9B5; font-size: 12px; line-height: 1.6; margin: 18px 0 0; font-family: ${FONT};" class="dark-text-beige">${inline(s.footnote)}</p>`
    : '';
  return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
<tbody><tr>
<td bgcolor="#6D1723" class="dark-hero-red-fix" style="background-color: #6D1723; border-radius: 14px; padding: 30px 28px; text-align: center;">
<p style="color: #FFFFFF; font-size: 20px; font-weight: 800; margin: 0 0 8px; font-family: ${FONT};" class="dark-text-white">${inline(s.title)}</p>
${text}${button}${foot}
</td>
</tr></tbody>
</table>`;
}

function renderSection(s: MailSection, isFirst: boolean): string {
  const top = isFirst ? '32px' : '24px';
  switch (s.type) {
    case 'heading':
      return row(sectionHeading(s.text), `${top} 44px 0`);
    case 'text': {
      const ps = paragraphs(s.text);
      if (ps.length === 0) return '';
      return row(ps.map((p, i) => bodyParagraph(inline(p), { align: s.align, last: i === ps.length - 1 })).join('\n'), `${top} 44px 8px`);
    }
    case 'box':
      if (!(s.title ?? '').trim() && !(s.text ?? '').trim()) return '';
      return row(accentBox(s.title, s.text, s.tone ?? 'info', 0), `${top} 44px 8px`);
    case 'cards': {
      const items = s.items.filter(i => (i.title ?? '').trim() || (i.text ?? '').trim());
      if (items.length === 0) return '';
      const head = s.heading?.trim() ? sectionHeading(s.heading) + '\n' : '';
      const cards = items.map((it, i) => accentBox(it.title, it.text, 'info', i === items.length - 1 ? 4 : 14)).join('\n');
      return row(head + cards, `${top} 44px 8px`);
    }
    case 'facts': {
      const rows = s.rows.filter(r => (r.label ?? '').trim() && (r.value ?? '').trim());
      if (rows.length === 0) return '';
      const head = s.heading?.trim() ? sectionHeading(s.heading) + '\n' : '';
      return row(head + factsTable(rows), `${top} 44px 8px`);
    }
    case 'steps': {
      const items = s.items.filter(i => (i ?? '').trim());
      if (items.length === 0) return '';
      const head = s.heading?.trim() ? sectionHeading(s.heading) + '\n' : '';
      return row(head + numberedSteps(items), `${top} 44px 8px`);
    }
    case 'list': {
      const items = s.items.filter(i => i.trim());
      if (items.length === 0) return '';
      const head = s.heading?.trim() ? sectionHeading(s.heading) + '\n' : '';
      return row(head + bulletList(items), `${top} 44px 8px`);
    }
    case 'cta':
      if (!safeUrl(s.url)) return '';
      return row(ctaBlock(s), `20px 44px 8px`);
  }
}

function heroBlock(h: MailHero): string {
  const subtitle = h.subtitle?.trim()
    ? `\n<p style="color: #FAF6EE; font-size: 16px; font-weight: 600; margin: 0 0 6px; font-family: ${FONT};" class="dark-text-cream">${inline(h.subtitle)}</p>`
    : '';
  const highlight = h.highlight?.trim()
    ? `\n<p style="color: #FAF6EE; font-size: 15px; font-weight: 700; margin: 0; font-family: ${FONT};" class="dark-text-cream">${inline(h.highlight)}</p>`
    : '';
  const emoji = h.emoji?.trim()
    ? `<div style="font-size: 46px; line-height: 1; margin: 0 0 16px; color: #FFFFFF;" class="dark-text-white">${escapeHtml(h.emoji.trim())}</div>\n`
    : '';
  const badge = h.badge?.trim()
    ? `<table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin: 0 auto 18px;">
<tbody>
<tr>
<td align="center" style="padding: 7px 22px; border: 1px solid #D4C9B5; border-radius: 50px; font-family: ${FONT};"><font color="#D4C9B5" face="${FONT}"><span style="color: #D4C9B5; font-size: 12px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; line-height: 1; font-family: ${FONT};" class="dark-text-beige">${inline(h.badge)}</span></font></td>
</tr>
</tbody>
</table>\n`
    : '';
  const bannerUrl = safeUrl(h.imageUrl);
  const hoehe = Math.round(Math.min(BANNER_MAX_HOEHE, Math.max(BANNER_MIN_HOEHE, h.imageHeight || 280)));
  const image = bannerUrl
    ? `
<!-- BANNER -->
<tr>
<td bgcolor="#FFFFFF" class="dark-card-fix" style="padding: 0; background-color: #FFFFFF; line-height: 0; font-size: 0;">
<img src="${escapeHtml(bannerUrl)}" width="620" height="${hoehe}" alt="${escapeHtml(h.imageAlt ?? '')}" style="display: block; width: 100%; max-width: 620px; height: auto; border: 0;">
</td>
</tr>`
    : '';
  const oben = h.imagePosition === 'oben';
  return `${oben ? image.trimStart() + '\n' : ''}<!-- HERO -->
<tr>
<td bgcolor="#6D1723" class="dark-hero-red-fix" style="padding: 44px 40px 36px; text-align: center; background-color: #6D1723;">
${emoji}${badge}<h1 style="color: #FFFFFF; font-size: 30px; font-weight: 800; line-height: 1.25; margin: 6px 0 8px; font-family: ${FONT};" class="dark-text-white">${inline(h.title)}</h1>${subtitle}${highlight}
</td>
</tr>${oben ? '' : image}`;
}

function contactBlock(c: MailDoc['contact'], mitEmoji: boolean): string {
  let second: string;
  if (c.text?.trim()) {
    second = inline(c.text);
  } else if (c.email?.trim()) {
    const mail = escapeHtml(c.email.trim());
    const where = c.place?.trim() ? `an den Tresen in ${escapeHtml(c.place.trim())}` : 'an den Tresen Eurer Sportschule';
    second = `Bei Fragen wendet Euch bitte ${where} oder schreibt an <a href="mailto:${mail}" style="color: #6D1723; text-decoration: underline;"><span style="color: #6D1723;" class="dark-text-red">${mail}</span></a>.`;
  } else {
    second = 'Bei Fragen wendet Euch bitte an den Tresen Eurer Sportschule.';
  }
  return `<!-- NO-REPLY -->
<tr>
<td bgcolor="#FFFFFF" class="dark-card-fix" style="padding: 20px 44px; background-color: #FFFFFF;">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
<tbody><tr>
<td bgcolor="#FAF6EE" class="dark-bg-fix" style="padding: 14px 20px; text-align: center; border: 1px solid #D4C9B5; border-radius: 8px; background-color: #FAF6EE;">
<p style="color: #6D1723; font-size: 11px; line-height: 1.5; margin: 0; font-family: ${FONT};" class="dark-text-red">
${mitEmoji ? '⚠️ ' : ''}<strong>Hinweis:</strong> Diese E-Mail wurde automatisch versendet. Bitte antwortet <strong>nicht</strong> auf diese Nachricht.<br><br>
${second}
</p>
</td>
</tr></tbody>
</table>
</td>
</tr>`;
}

const FOOTER = `<!-- FOOTER -->
<tr>
<td bgcolor="#FAF6EE" class="dark-bg-fix" style="padding: 32px 40px; text-align: center; border-top: 1px solid #D4C9B5; border-radius: 0 0 16px 16px; background-color: #FAF6EE;">
<img src="${LOGO_URL}" width="90" height="49" style="display: inline-block; max-width: 90px; height: auto; margin-bottom: 10px; border: 0;" alt="Randori Pro">
<p style="color: #575E62; font-size: 12px; margin: 0 0 16px; font-family: ${FONT};" class="dark-text-grey">Kampfsport · Selbstverteidigung · Fitness</p>
<div style="margin: 14px 0 22px; text-align: center;">
<a href="https://www.facebook.com/RandoriPro" style="display: inline-block; margin: 0 8px; text-decoration: none;">
<img src="https://img.icons8.com/color/96/facebook-new.png" width="44" height="44" alt="Facebook" style="display: block; width: 44px; height: 44px; border: 0;">
</a>
<a href="https://www.instagram.com/randori.pro.berlin/" style="display: inline-block; margin: 0 8px; text-decoration: none;">
<img src="https://img.icons8.com/color/96/instagram-new--v1.png" width="44" height="44" alt="Instagram" style="display: block; width: 44px; height: 44px; border: 0;">
</a>
<a href="https://www.youtube.com/@randoripro" style="display: inline-block; margin: 0 8px; text-decoration: none;">
<img src="https://img.icons8.com/color/96/youtube-play.png" width="44" height="44" alt="YouTube" style="display: block; width: 44px; height: 44px; border: 0;">
</a>
</div>
<div style="margin: 12px 0 16px;">
<a href="https://randori-pro.de" style="color: #575E62; font-size: 12px; text-decoration: none; margin: 0 8px;"><span style="color: #575E62;" class="dark-text-grey">Website</span></a>
<a href="https://randori-pro.de/impressum" style="color: #575E62; font-size: 12px; text-decoration: none; margin: 0 8px;"><span style="color: #575E62;" class="dark-text-grey">Impressum</span></a>
<a href="https://randori-pro.de/datenschutz" style="color: #575E62; font-size: 12px; text-decoration: none; margin: 0 8px;"><span style="color: #575E62;" class="dark-text-grey">Datenschutz</span></a>
</div>
<div style="border-top: 1px solid #D4C9B5; padding-top: 14px; margin-top: 4px;">
<p style="color: #575E62; font-size: 9px; line-height: 1.5; text-align: center; margin: 0; font-family: ${FONT};" class="dark-text-grey">
Randori Pro Management GmbH · Vertr. d. Lutz Heyden · Innsbrucker Str. 4, 10825 Berlin · Tel: +49 172 / 7090953 · backoffice@randori-pro.de · USt-IdNr.: DE 357953850 · V.i.S.d.P.: Dominik Krüger
</p>
<p style="color: #575E62; font-size: 9px; line-height: 1.5; text-align: center; margin: 6px 0 0;" class="dark-text-grey">
Keine Mails mehr erhalten? <a href="{{ unsubscribe }}" style="color: #575E62;"><span style="color: #575E62;" class="dark-text-grey">Abmeldung</span></a>
</p>
</div>
</td>
</tr>`;

const HEAD_STYLE = `<style type="text/css">
:root { color-scheme: light only; supported-color-schemes: light only; }
@media (prefers-color-scheme: dark) {
body, table, td, div, p, h1, h2, h3, span, a {
color: inherit !important; background-color: inherit !important;
}
.dark-bg-fix { background-color: #FAF6EE !important; }
.dark-card-fix { background-color: #FFFFFF !important; }
.dark-hero-red-fix { background-color: #6D1723 !important; }
.dark-signature-fix { background-color: #D4C9B5 !important; }
.dark-btn-fix { background-color: #FAF6EE !important; }
.dark-text-dark { color: #1A1414 !important; }
.dark-text-red { color: #6D1723 !important; }
.dark-text-accent { color: #DC0D1D !important; }
.dark-mark-fix { background-color: #D4C9B5 !important; color: #1A1414 !important; }
.dark-text-body { color: #333333 !important; }
.dark-text-grey { color: #575E62 !important; }
.dark-text-white { color: #FFFFFF !important; }
.dark-text-cream { color: #FAF6EE !important; }
.dark-text-beige { color: #D4C9B5 !important; }
}
[data-ogsc] body, [data-ogsc] table, [data-ogsc] div {
background-color: inherit !important; color: inherit !important;
}
a[x-apple-data-detectors] {
color: inherit !important; text-decoration: none !important;
font-size: inherit !important; font-family: inherit !important;
font-weight: inherit !important; line-height: inherit !important;
}
</style>`;

/** Die komplette Mail als HTML — genau das, was nach Brevo geht. */
export function renderMail(doc: MailDoc): string {
  const intro = paragraphs(doc.intro);
  const greeting = doc.greeting?.trim()
    ? `<p style="font-size: 19px; font-weight: 700; line-height: 1.5; margin: 0 0 18px; font-family: ${FONT}; color: #6D1723; text-align: center;" class="dark-text-red">${inline(doc.greeting)}</p>\n`
    : '';
  const introBlock = greeting || intro.length
    ? `<!-- INTRO -->\n` + row(greeting + intro.map((p, i) => bodyParagraph(inline(p), { last: i === intro.length - 1 })).join('\n'), '36px 44px 8px')
    : '';
  const sections = doc.sections
    .map((s, i) => renderSection(s, i === 0 && !introBlock))
    .filter(Boolean)
    .join('\n');
  const closing = doc.closing?.trim()
    ? `<!-- CLOSING -->\n` + row(bodyParagraph(inline(doc.closing), { last: true }), '28px 44px 32px')
    : `<tr><td bgcolor="#FFFFFF" class="dark-card-fix" style="padding: 0 0 24px; background-color: #FFFFFF; font-size: 0; line-height: 0;">&nbsp;</td></tr>`;
  const preheader = doc.preheader?.trim()
    ? `<div style="display: none; max-height: 0; overflow: hidden; mso-hide: all; font-size: 1px; line-height: 1px; color: #FAF6EE;">
${escapeHtml(ohneFormat(doc.preheader).trim())}
</div>\n`
    : '';

  return `<!DOCTYPE html>
<html lang="de" xmlns="http://www.w3.org/1999/xhtml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="x-apple-disable-message-reformatting">
<meta name="format-detection" content="telephone=no, date=no, address=no, email=no, url=no">
<meta name="color-scheme" content="light only">
<meta name="supported-color-schemes" content="light only">
<title>${escapeHtml(ohneFormat(doc.title))}</title>
<!--[if mso]>
<xml><o:OfficeDocumentSettings><o:AllowPNG/><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml>
<![endif]-->
${HEAD_STYLE}
</head>
<body class="dark-bg-fix" bgcolor="#FAF6EE" style="margin: 0; padding: 0; font-family: ${FONT}; color-scheme: light only; background-color: #FAF6EE !important;">
${preheader}<div style="padding: 28px 0; background-color: #FAF6EE; font-family: ${FONT};" class="dark-bg-fix">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" bgcolor="#FAF6EE" style="border-collapse: collapse; background-color: #FAF6EE; font-family: ${FONT};">
<tbody><tr><td align="center" bgcolor="#FAF6EE" style="background-color: #FAF6EE;">
<!--[if mso]>
<table role="presentation" width="620" cellspacing="0" cellpadding="0" border="0" align="center"><tr><td bgcolor="#FFFFFF">
<![endif]-->
<table role="presentation" width="620" cellspacing="0" cellpadding="0" border="0" bgcolor="#FFFFFF" class="dark-card-fix" style="max-width: 620px; width: 100%; border-radius: 16px; box-shadow: rgba(109, 23, 35, 0.1) 0px 6px 32px; font-family: ${FONT}; border-collapse: separate; background-color: #FFFFFF;">
<tbody>

<!-- LOGO BAR -->
<tr>
<td bgcolor="#FFFFFF" class="dark-card-fix" style="padding: 20px 40px; text-align: center; border-radius: 16px 16px 0 0; background-color: #FFFFFF;">
<img src="${LOGO_URL}" width="120" height="65" style="display: inline-block; max-width: 120px; height: auto; border: 0;" alt="Randori Pro Logo">
</td>
</tr>

${heroBlock(doc.hero)}

${introBlock}
${sections}
${closing}

<!-- SIGNATUR -->
<tr>
<td bgcolor="#D4C9B5" class="dark-signature-fix" style="padding: 32px 44px; text-align: center; border-top: 3px solid #6D1723; background-color: #D4C9B5;">
<p style="color: #1A1414; font-size: 14px; font-weight: 600; margin: 0 0 4px; font-family: ${FONT};" class="dark-text-dark">${inline(doc.signature.greeting)}</p>
<p style="color: #6D1723; font-size: 13px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; margin: 0; font-family: ${FONT};" class="dark-text-red">${inline(doc.signature.team)}</p>
</td>
</tr>

${contactBlock(doc.contact, doc.emojis !== 'ohne')}

${FOOTER}

</tbody>
</table>
<!--[if mso]>
</td></tr></table>
<![endif]-->
</td></tr></tbody>
</table>
</div>
</body>
</html>`;
}
